import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { Shield, Smartphone, LogOut, LayoutDashboard } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const StandardUserRedirectPage = () => {
  const { signOut, user } = useAuth();
  const navigate = useNavigate();

  // Check if user is an admin (has roles other than just standard user role 5)
  const isAdmin = user?.roleIds?.some((roleId) => roleId !== 5) || false;

  const handleLogout = async () => {
    try {
      await signOut();
      // Navigate to login page after successful logout
      navigate("/admin/login");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const handleGotoDashboard = () => {
    navigate("/admin/dashboard");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full shadow-lg">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <Shield className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            {isAdmin ? "Admin Portal Notice" : "Admin Portal Access Restricted"}
          </CardTitle>
          <CardDescription className="text-center">
            {isAdmin
              ? "You have access to both the admin portal and the mobile app"
              : "This portal is designed for admin users only"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex justify-center items-center mb-2 gap-2">
              <h3 className="font-medium text-center">
                Hello, {user?.name || "User"}
              </h3>
              {isAdmin && (
                <Badge variant="secondary" className="ml-2">
                  Admin
                </Badge>
              )}
            </div>
            <p className="text-sm text-center text-muted-foreground">
              {isAdmin
                ? "You have administrative privileges that grant you access to the admin portal. You can also use our mobile app for a more convenient experience on the go."
                : "Your account has standard user permissions, which doesn't have access to the admin portal. Please use our mobile app for the best experience."}
            </p>
          </div>

          {isAdmin ? (
            <div className="space-y-4">
              <Button
                className="w-full flex items-center justify-center gap-2"
                onClick={handleGotoDashboard}
              >
                <LayoutDashboard className="h-4 w-4" />
                Go to Admin Dashboard
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-muted-foreground/20" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-background px-2 text-muted-foreground">
                    OR USE OUR MOBILE APP
                  </span>
                </div>
              </div>
            </div>
          ) : null}

          <div className="space-y-3">
            <h4 className="font-medium flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              Download Our App
            </h4>
            <p className="text-sm text-muted-foreground">
              The Hriatna Eng app provides a better experience for users with
              access to all content and features designed for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <Button
                className="flex-1 gap-2 bg-black text-white hover:bg-black/90"
                size="lg"
                onClick={() =>
                  window.open("https://apps.apple.com/app/hriatnaeng", "_blank")
                }
              >
                <img
                  src="/app-store.svg"
                  alt="Download on App Store"
                  className="h-6"
                />
                <div className="flex flex-col items-start">
                  <span className="text-xs">Download on the</span>
                  <span className="text-sm font-semibold">App Store</span>
                </div>
              </Button>
              <Button
                variant="outline"
                className="flex-1 gap-2"
                size="lg"
                onClick={() =>
                  window.open(
                    "https://play.google.com/store/apps/details?id=com.hriatnaeng",
                    "_blank"
                  )
                }
              >
                <img
                  src="/google-play.svg"
                  alt="Get it on Google Play"
                  className="h-6"
                />
                <div className="flex flex-col items-start">
                  <span className="text-xs">Get it on</span>
                  <span className="text-sm font-semibold">Google Play</span>
                </div>
              </Button>
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button
            variant="ghost"
            className="w-full flex items-center gap-2 text-muted-foreground hover:text-destructive"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            Log Out
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default StandardUserRedirectPage;
